import { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';
import { useExtensionDetection } from '../../hooks/useExtensionDetection';
import { CHROME_EXTENSION_URL } from '../../lib/constants';
import { useInstallReminderDismissal } from '../../hooks/useInstallReminderDismissal';
import { ChromeIcon } from '../shared/ChromeIcon';
import { capture } from '@/lib/analytics';
import { log } from '@/lib/logger';

type CardState = 'not-installed' | 'celebrating' | 'fading-out' | 'hidden';

export function ExtensionInstallCard() {
  const { detected, checking } = useExtensionDetection({ poll: true, intervalMs: 3000 });
  const [cardState, setCardState] = useState<CardState>('hidden');
  const { dismissed, dismiss } = useInstallReminderDismissal();

  useEffect(() => {
    if (checking) return;

    if (detected && cardState === 'not-installed') {
      log.info('[ExtensionInstallCard] Extension detected, celebrating');
      setCardState('celebrating');
    }

    if (!detected && cardState === 'hidden') {
      setCardState('not-installed');
    }
  }, [detected, checking, cardState]);

  // Start the hold after entering this state. Scheduling it in the detection
  // effect cancels it when that effect cleans up on the transition itself.
  useEffect(() => {
    if (cardState !== 'celebrating') return;
    const holdTimer = setTimeout(() => setCardState('fading-out'), 3000);
    return () => clearTimeout(holdTimer);
  }, [cardState]);

  useEffect(() => {
    if (cardState === 'fading-out') {
      const fadeTimer = setTimeout(() => {
        setCardState('hidden');
      }, 300);
      return () => clearTimeout(fadeTimer);
    }
  }, [cardState]);

  if (cardState === 'hidden' || dismissed) {
    return null;
  }

  if (cardState === 'celebrating' || cardState === 'fading-out') {
    return (
      <section
        className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-3 transition-opacity duration-300"
        style={{ opacity: cardState === 'fading-out' ? 0 : 1 }}
        aria-label="Extension installed"
      >
        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-green-600 flex-shrink-0" />
        <p className="text-sm text-green-800 flex-1">
          <span className="font-semibold">You're all set!</span> OverlayQA is installed and ready to go.
        </p>
        <button
          onClick={() => setCardState('fading-out')}
          className="flex-shrink-0 text-green-600 hover:text-green-800 transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </section>
    );
  }

  return (
    <section
      className="relative card-sm card-brand pr-12 sm:pr-4 flex flex-col sm:flex-row sm:items-center gap-3"
      aria-label="Install extension"
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <ChromeIcon className="w-5 h-5 mt-1 shrink-0" />
        <div>
          <h2 className="text-sm font-semibold text-white">Capture issues on localhost and staging</h2>
          <p className="text-sm text-white mt-1">
            Click an element on any page you can open in Chrome, even behind a login. No public page needed.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          const params = new URLSearchParams(window.location.search);
          capture('dashboard_extension_install_click', {
            source: 'install_card',
            utm_variant: params.get('utm_variant') || null,
            utm_source: params.get('utm_source') || null,
          });
          window.open(CHROME_EXTENSION_URL, '_blank', 'noopener');
        }}
        // Stacked (below sm) the CTA is its own row and lines up with the text
        // column, not the icon: w-5 icon + gap-3 = 32px = ml-8. In the row it is
        // the last item, so the indent goes.
        className="btn-primary ml-8 sm:ml-0 self-start sm:self-auto shrink-0"
      >
        <span className="text-cap-trim">Install Chrome extension</span>
      </button>
      {/* Absolute in the top-right corner while the card stacks; a flex item
          centred with the CTA once the card is a row (sm+), so the X no longer
          sits 12px above the button it belongs beside. */}
      <button
        type="button"
        className="btn-ghost text-white hover:bg-white/15 hover:text-white absolute top-3 right-2 sm:static shrink-0"
        aria-label="Dismiss install reminder"
        onClick={() => {
          capture('dashboard_extension_install_dismissed', { source: 'install_card' });
          dismiss();
        }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </section>
  );
}
